import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Settings, 
  Users, 
  Key, 
  Clock, 
  Save,
  Trash2,
  Shield,
  Building2,
  RefreshCw,
  Plus,
  Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import CreateUserDialog from "../components/admin/CreateUserDialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSettings() {
  const { darkMode } = useOutletContext() || { darkMode: true };
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [schedulerConfig, setSchedulerConfig] = useState({
    is_enabled: true,
    interval_hours: 72
  });
  const [apiKeys, setApiKeys] = useState({
    use_custom_key: false,
    openai_api_key: ""
  });

  // Create user form
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    password: "",
    role: "business",
    brand: "",
    region: ""
  });

  const brands = [
    { value: "blackswan", label: "BlackSwan Business Setup" },
    { value: "whiteswan", label: "WhiteSwan Dental Clinic" },
    { value: "blackswan_realestate", label: "BlackSwan Real Estate" },
    { value: "prazi", label: "Prazi Medical" }
  ];

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user.role === "admin";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch users (admin only)
      if (isAdmin) {
        const usersRes = await axios.get(`${API}/auth/users`);
        setUsers(usersRes.data);
      }
      
      // Fetch scheduler config
      const schedulerRes = await axios.get(`${API}/scheduler/config`);
      setSchedulerConfig(schedulerRes.data);
      
      // Fetch API keys config
      const apiKeysRes = await axios.get(`${API}/settings/api-keys`);
      setApiKeys(apiKeysRes.data);
      
    } catch (error) {
      console.error("Error fetching admin data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/auth/users/${userId}`);
      toast.success("User deleted");
      setUsers(users.filter(u => u.id !== userId));
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      const userData = {
        ...newUser,
        brand: newUser.brand || null,
        region: (newUser.region && newUser.region !== "__all__") ? newUser.region : null
      };
      
      await axios.post(`${API}/auth/register`, userData);
      toast.success(`User ${newUser.name} created successfully`);
      
      // Refresh users list
      const usersRes = await axios.get(`${API}/auth/users`);
      setUsers(usersRes.data);
      
      // Reset form and close dialog
      setNewUser({
        name: "",
        email: "",
        password: "",
        role: "business",
        brand: "",
        region: ""
      });
      setCreateUserOpen(false);
      
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to create user";
      toast.error(message);
    } finally {
      setCreating(false);
    }
  };

  const handleSaveScheduler = async () => {
    try {
      await axios.put(`${API}/scheduler/config`, schedulerConfig);
      toast.success("Scheduler settings saved");
    } catch (error) {
      toast.error("Failed to save scheduler settings");
    }
  };

  const handleSaveApiKeys = async () => {
    try {
      await axios.put(`${API}/settings/api-keys`, apiKeys);
      toast.success("API keys saved");
    } catch (error) {
      toast.error("Failed to save API keys");
    }
  };

  const getRoleBadgeColor = (role) => {
    return role === "admin" 
      ? "bg-purple-500/20 text-purple-400 border-0"
      : "bg-blue-500/20 text-blue-400 border-0";
  };

  const getBrandBadgeColor = (brand) => {
    const colors = {
      blackswan: "bg-[#DC143C]/20 text-[#DC143C]",
      whiteswan: "bg-blue-500/20 text-blue-400",
      prazi: "bg-emerald-500/20 text-emerald-400",
      blackswan_realestate: "bg-amber-500/20 text-amber-400"
    };
    return colors[brand] || "bg-gray-500/20 text-gray-400";
  };

  if (!isAdmin) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card className={`${darkMode ? 'bg-[#161b22] border-[#30363d]' : 'bg-white'}`}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <Shield className="w-5 h-5 text-red-500" />
              Access Denied
            </CardTitle>
            <CardDescription>
              You need admin privileges to access this page.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Admin Settings
          </h1>
          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Manage users, API keys, and system settings
          </p>
        </div>
        <Badge className="bg-purple-500/20 text-purple-400 border-0">
          <Shield className="w-3 h-3 mr-1" />
          Admin
        </Badge>
      </div>

      <Tabs defaultValue="users" className="w-full">
        <TabsList className={`${darkMode ? 'bg-[#21262d]' : 'bg-gray-100'}`}>
          <TabsTrigger value="users">
            <Users className="w-4 h-4 mr-2" />
            Users
          </TabsTrigger>
          <TabsTrigger value="api-keys">
            <Key className="w-4 h-4 mr-2" />
            API Keys
          </TabsTrigger>
          <TabsTrigger value="scheduler">
            <Clock className="w-4 h-4 mr-2" />
            Scheduler
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="mt-4">
          <Card className={`${darkMode ? 'bg-[#161b22] border-[#30363d]' : 'bg-white'}`}>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className={darkMode ? 'text-white' : 'text-gray-900'}>
                  User Management
                </CardTitle>
                <CardDescription>
                  View and manage all registered users
                </CardDescription>
              </div>
              
              {/* Create User Dialog */}
              <CreateUserDialog
                open={createUserOpen}
                onOpenChange={setCreateUserOpen}
                darkMode={darkMode}
                newUser={newUser}
                setNewUser={setNewUser}
                brands={brands}
                creating={creating}
                handleCreateUser={handleCreateUser}
              />
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className={darkMode ? 'border-[#30363d]' : ''}>
                    <TableHead className={darkMode ? 'text-gray-400' : ''}>Name</TableHead>
                    <TableHead className={darkMode ? 'text-gray-400' : ''}>Email</TableHead>
                    <TableHead className={darkMode ? 'text-gray-400' : ''}>Role</TableHead>
                    <TableHead className={darkMode ? 'text-gray-400' : ''}>Brand</TableHead>
                    <TableHead className={darkMode ? 'text-gray-400' : ''}>Region</TableHead>
                    <TableHead className={darkMode ? 'text-gray-400' : ''}>Status</TableHead>
                    <TableHead className={darkMode ? 'text-gray-400' : ''}>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} className={darkMode ? 'border-[#30363d]' : ''}>
                      <TableCell className={darkMode ? 'text-white' : ''}>{u.name}</TableCell>
                      <TableCell className={darkMode ? 'text-gray-400' : 'text-gray-600'}>{u.email}</TableCell>
                      <TableCell>
                        <Badge className={getRoleBadgeColor(u.role)}>
                          {u.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {u.brand ? (
                          <Badge className={getBrandBadgeColor(u.brand)}>
                            {u.brand}
                          </Badge>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {u.region ? (
                          <Badge variant="outline" className={darkMode ? 'border-[#30363d] text-gray-300' : ''}>
                            🔒 {u.region}
                          </Badge>
                        ) : (
                          <span className="text-gray-500">All</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={u.is_active 
                          ? "bg-green-500/20 text-green-400 border-0" 
                          : "bg-red-500/20 text-red-400 border-0"
                        }>
                          {u.is_active ? "Active" : "Disabled"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {u.id !== user.id && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-400">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
                              <AlertDialogHeader>
                                <AlertDialogTitle className={darkMode ? 'text-white' : ''}>
                                  Delete User
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete {u.name}? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction 
                                  onClick={() => handleDeleteUser(u.id)}
                                  className="bg-red-500 hover:bg-red-600"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* API Keys Tab */}
        <TabsContent value="api-keys" className="mt-4">
          <Card className={`${darkMode ? 'bg-[#161b22] border-[#30363d]' : 'bg-white'}`}>
            <CardHeader>
              <CardTitle className={darkMode ? 'text-white' : 'text-gray-900'}>
                API Configuration
              </CardTitle>
              <CardDescription>
                Configure API keys for AI services
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label className={darkMode ? 'text-gray-300' : ''}>Use Custom API Key</Label>
                  <p className="text-sm text-gray-500">
                    Override the default Emergent LLM key with your own OpenAI key
                  </p>
                </div>
                <Switch
                  checked={apiKeys.use_custom_key}
                  onCheckedChange={(checked) => setApiKeys({...apiKeys, use_custom_key: checked})}
                />
              </div>
              
              {apiKeys.use_custom_key && (
                <div className="space-y-2">
                  <Label className={darkMode ? 'text-gray-300' : ''}>OpenAI API Key</Label>
                  <Input
                    type="password"
                    value={apiKeys.openai_api_key}
                    onChange={(e) => setApiKeys({...apiKeys, openai_api_key: e.target.value})}
                    placeholder="sk-..."
                    className={darkMode ? 'bg-[#21262d] border-[#30363d] text-white' : ''}
                  />
                </div>
              )}
              
              <Button onClick={handleSaveApiKeys} className="bg-[#DC143C] hover:bg-[#b91031]">
                <Save className="w-4 h-4 mr-2" />
                Save API Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Scheduler Tab */}
        <TabsContent value="scheduler" className="mt-4">
          <Card className={`${darkMode ? 'bg-[#161b22] border-[#30363d]' : 'bg-white'}`}>
            <CardHeader>
              <CardTitle className={darkMode ? 'text-white' : 'text-gray-900'}>
                Intelligence Scan Scheduler
              </CardTitle>
              <CardDescription>
                Configure automatic intelligence scanning
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label className={darkMode ? 'text-gray-300' : ''}>Enable Automatic Scans</Label>
                  <p className="text-sm text-gray-500">
                    Automatically run intelligence scans at regular intervals
                  </p>
                </div>
                <Switch
                  checked={schedulerConfig.is_enabled}
                  onCheckedChange={(checked) => setSchedulerConfig({...schedulerConfig, is_enabled: checked})}
                />
              </div>
              
              <div className="space-y-2">
                <Label className={darkMode ? 'text-gray-300' : ''}>Scan Interval (hours)</Label>
                <Input
                  type="number"
                  value={schedulerConfig.interval_hours}
                  onChange={(e) => setSchedulerConfig({...schedulerConfig, interval_hours: parseInt(e.target.value)})}
                  min={1}
                  max={168}
                  className={`w-32 ${darkMode ? 'bg-[#21262d] border-[#30363d] text-white' : ''}`}
                />
                <p className="text-sm text-gray-500">
                  Current setting: Every {schedulerConfig.interval_hours} hours ({(schedulerConfig.interval_hours / 24).toFixed(1)} days)
                </p>
              </div>
              
              <Button onClick={handleSaveScheduler} className="bg-[#DC143C] hover:bg-[#b91031]">
                <Save className="w-4 h-4 mr-2" />
                Save Scheduler Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
